<script setup>
  import BaseDialog from "@/base-components/Dialog/BaseDialog.vue";
  import Button from "@/base-components/Button";
  import { uri } from "@/constants/config";
  import InputText from "@/base-components/Form/InputText.vue";
  import { Form } from "vee-validate";
  import { onMounted, ref } from "vue";
  import { object } from "yup";
  import { useFetch } from "@/composables/useFetch";
  import useValidate from "@/composables/useValidate.js";
  import { useI18n } from "vue-i18n";
  import Lucide from "@/base-components/Lucide";
  import { toast } from "vue3-toastify";
  import { FormLabel, FormSwitch } from "@/base-components/Form";
  import BaseInputSelect from "@/base-components/Form/BaseInputSelect.vue";

  const props = defineProps({
    show: {
      type: Boolean,
      default: false,
    },
    user: {
      required: true,
    },
    role: {
      type: String,
      default: "admin",
    },
  });
  const { t: $t } = useI18n();
  const emit = defineEmits(["close", "onSuccess"]);

  function close() {
    emit("close");
  }

  function onSuccess() {
    try {
    } catch (error) {
    } finally {
    }
    emit("onSuccess");
  }

  const slug = ref(null);
  const data = null;
  const date = "";

  const loadingFlag = ref(false);
  const errorMessage = ref("");

  const successMessage = ref("");
  const mobile = ref(null);
  const otp = ref(null);

  const form = ref({
    mobile: "",
    email: "",
    isBlocked: false,
    isEmailValidated: false,
    isMobileValidated: false,
  });
  const accountType = [
    { value: "REAL", title: "Real" },
    { value: "LEGAL", title: "Legal" },
    { value: "SUPPORT", title: "Support" },
  ];
  const validationSchemaRegister = object(useValidate(["mobile", "email"]));

  async function register() {
    loadingFlag.value = true;
    successMessage.value = "";
    errorMessage.value = "";

    let url;
    if (props.role === "admin") {
      url = `${uri.admin.user.validation}/${props.user.id}`;
    } else if (role === "cloud") {
      url = `${uri.admin.cloud.user.validation}/${props.user.id}`;
    }

    try {
      // Await the result of useFetch
      const { data, error } = await useFetch(url, {
        method: "PATCH",
        body: form.value,
      });
      if (error) {
        errorMessage.value =
          error.message || "An error occurred while logging in.";
      } else {
        emit("onSuccess");
        close();
      }
    } catch (err) {
      errorMessage.value = err.message || "An unexpected error occurred.";
    } finally {
      loadingFlag.value = false;
    }
  }

  onMounted(async () => {
    Object.assign(form.value, props.user);
  });
</script>

<template>
  <section>
    <BaseDialog
      :size="'lg'"
      staticBackdrop
      :open="show"
      :has-footer="true"
      :title="$t('title.update')"
      @close="close"
    >
      <div class="rounded-md px-2 py-2 w-full max-h-[78vh] overflow-auto mb-5">
        <Form
          class="w-full"
          id="registerForm"
          :validation-schema="validationSchemaRegister"
          @submit="register"
        >
          <input type="password" class="hidden" />
          <input type="text" name="username" class="hidden" />
          <input type="text" name="mobile" class="hidden" />
          <input type="text" name="email" class="hidden" />
          <div class="px-2">
            <div class="grid grid-cols-12 gap-4 mt-5 gap-y-5">
              <!-- Mobile -->
              <div
                class="col-span-12 md:col-span-6 lg:col-span-6 xl:col-span-6 intro-y"
              >
                <InputText
                  name="mobile"
                  :dir="'ltr'"
                  :has-label="true"
                  :label="$t('label.mobile')"
                  v-model="form.mobile"
                  id="input-wizard-3"
                  type="text"
                />
              </div>

              <!-- Email -->
              <div
                class="col-span-12 md:col-span-6 lg:col-span-6 xl:col-span-6 intro-y"
              >
                <InputText
                  name="email"
                  :dir="'ltr'"
                  :has-label="true"
                  :label="$t('label.email')"
                  v-model="form.email"
                  id="input-wizard-3"
                  type="text"
                />
              </div>

              <!-- Account Type -->
              <div class="col-span-12 intro-y grid grid-cols-3 my-2">
                <div class="flex-wrap border-gray p-2 rounded-s-md border">
                  <span class="text-sm mb-1 mx-3">
                    {{ $t("label.is-block") }}
                  </span>
                  <FormSwitch class="flex flex-row items-start" dir="ltr">
                    <FormSwitch.Input
                      id="post-form-6"
                      type="checkbox"
                      class="xs"
                      v-model="form.isBlocked"
                    />
                  </FormSwitch>
                </div>
                <div class="flex-wrap border-gray p-2 border-y">
                  <span class="text-sm mb-1 mx-3">
                    {{ $t("label.email-validation") }}
                  </span>
                  <FormSwitch class="flex flex-row items-start" dir="ltr">
                    <FormSwitch.Input
                      id="post-form-6"
                      type="checkbox"
                      class="xs"
                      v-model="form.isEmailValidated"
                    />
                  </FormSwitch>
                </div>
                <div class="flex-wrap border-gray p-2 rounded-l-md border">
                  <span class="text-sm mb-1 mx-3">
                    {{ $t("label.mobile-validation") }}
                  </span>
                  <FormSwitch class="flex flex-row items-start" dir="ltr">
                    <FormSwitch.Input
                      id="post-form-6"
                      type="checkbox"
                      class="xs"
                      v-model="form.isMobileValidated"
                    />
                  </FormSwitch>
                </div>
              </div>
            </div>
          </div>
        </Form>
      </div>

      <template v-slot:footer>
        <free-style-shimmer
          width="100%"
          height="25px"
          class="w-full"
          v-if="loadingFlag"
        />
        <div class="flex col-md-12 items-end justify-end gap-2" v-else>
          <Button
            variant="outline-secondary"
            @click="close"
            size="sm"
            class="shadow-md justify-end items-end"
          >
            {{ $t("button.cancel") }}
          </Button>
          <Button
            variant="primary"
            size="sm"
            form="registerForm"
            class="shadow-md justify-end items-end"
          >
            {{ $t("button.register") }}
          </Button>
        </div>
      </template>
    </BaseDialog>
  </section>
</template>

<style scoped></style>
